import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export type SupportedAppTimezone = "UTC" | "America/Vancouver";
export const APP_TIMEZONE: SupportedAppTimezone = "America/Vancouver";

/* Shared numeric + shift-window constants — mirrors backend app.constant.ts */
export const COMMON_VALUES = {
    number: {
        zero: 0,
        one: 1,
        two: 2,
        three: 3,
        four: 4,
        twenty_three: 23,
        twenty_four: 24,
        fifty_nine: 59,
        sixty: 60,
        three_hundred: 300,
        one_thousand: 1000,
    },
} as const;
export const HOUR_24 = COMMON_VALUES.number.twenty_four;
export const MS_PER_MINUTE = COMMON_VALUES.number.sixty * COMMON_VALUES.number.one_thousand;
export const SHIFT_TIME_PARTS_COUNT = {
    min: COMMON_VALUES.number.two,
    max: COMMON_VALUES.number.three,
} as const;
export const SHIFT_HOUR_RANGE = {
    min: COMMON_VALUES.number.zero,
    max: COMMON_VALUES.number.twenty_three,
} as const;
export const SHIFT_MINUTE_RANGE = {
    min: COMMON_VALUES.number.zero,
    max: COMMON_VALUES.number.fifty_nine,
} as const;
export const EDIT_WINDOW_LEEWAY_MS = COMMON_VALUES.number.two * COMMON_VALUES.number.sixty * COMMON_VALUES.number.sixty * COMMON_VALUES.number.one_thousand;
export const MAX_DELETE_TASK_NAME_LENGTH = COMMON_VALUES.number.sixty;
export const HOLD_DELAY_MS = COMMON_VALUES.number.three_hundred;
export const MOVE_THRESHOLD_PX = 8;
export const SCROLL_EDGE_PX = 48;
export const SCROLL_STEP_PX = 12;
export const DRAG_CLICK_BLOCK_DURATION_MS = 500;
export const DRAG_SCROLL_INTERVAL_MS = 16;
export const DRAGGED_TASK_Z_INDEX = COMMON_VALUES.number.one;
export const DRAGGED_TASK_OPACITY = 0.7;
export const TASK_DROP_TARGET_OUTLINE = '2px solid var(--color-brand, #38a169)';
export const TASK_TABLE_COLUMN_COUNT = 7;

export function getAppTimezone(): SupportedAppTimezone {
  return APP_TIMEZONE;
}

export function formatDateForTimezone(
  date: Date = new Date(),
  timezone: SupportedAppTimezone = getAppTimezone()
): string {
  if (timezone === "UTC") {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const dateParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const year = dateParts.find((part) => part.type === "year")?.value;
  const month = dateParts.find((part) => part.type === "month")?.value;
  const day = dateParts.find((part) => part.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

export function formatDateForAppTimezone(date: Date = new Date()): string {
  return formatDateForTimezone(date, APP_TIMEZONE);
}

export function formatDateOnlyForAppTimezone(
  value: Date | string | null | undefined
): string {
  if (!value) {
    return formatDateForAppTimezone();
  }

  const dateOnlyAnchor = createDateOnlyUtcAnchor(value);
  if (!dateOnlyAnchor) {
    return formatDateForAppTimezone(value instanceof Date ? value : new Date(value));
  }

  return formatDateForAppTimezone(dateOnlyAnchor);
}

export function addDaysForAppTimezone(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function formatTimeForAppTimezone(
  value: Date | string | null | undefined,
  fallback = "--:--"
): string {
  if (!value) {
    return fallback;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date).toLowerCase();
}

// Format a bare "HH:MM" or "HH:MM:SS" time string (e.g. from a PG TIME column) as "5:00 am".
// Pass {compact: true} for "5:00am" (no space before am/pm).
export function formatTimeTo12Hour(time: string | null | undefined, options?: { compact?: boolean }): string {
  if(!time) return "--:--";
  const [hourStr, minuteStr] = time.split(":");
  const hour = parseInt(hourStr, 10);
  const minute = minuteStr || "00";
  if(isNaN(hour)) return "--:--";
  const period = hour >= 12 ? "pm" : "am";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const separator = options?.compact ? "" : " ";
  return `${hour12}:${minute}${separator}${period}`;
}

const extractDateOnlyMidnightUtc = (
  value: Date | string,
): { year: number; month: number; day: number } | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();

    // Pure DATE string from backend (YYYY-MM-DD)
    const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      if (year && month && day) {
        return { year, month, day };
      }
      return null;
    }

    // Midnight-UTC ISO string typically produced when DATE is serialized via Date object
    const midnightUtcMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.\d{1,3})?Z$/,
    );
    if (midnightUtcMatch) {
      const year = Number(midnightUtcMatch[1]);
      const month = Number(midnightUtcMatch[2]);
      const day = Number(midnightUtcMatch[3]);
      if (year && month && day) {
        return { year, month, day };
      }
    }

    return null;
  }

  if (
    value.getUTCHours() === 0 &&
    value.getUTCMinutes() === 0 &&
    value.getUTCSeconds() === 0 &&
    value.getUTCMilliseconds() === 0
  ) {
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
    };
  }

  return null;
};

export function createDateOnlyUtcAnchor(
  value: Date | string | null | undefined,
): Date | null {
  if (!value) {
    return null;
  }

  const dateOnly = extractDateOnlyMidnightUtc(value);

  if (dateOnly) {
    return new Date(Date.UTC(dateOnly.year, dateOnly.month - 1, dateOnly.day, 12, 0, 0, 0));
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12, 0, 0, 0));
}

export function formatDisplayDateForAppTimezone(
  value: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  },
  fallback = ""
): string {
  if (!value) {
    return fallback;
  }

  const dateOnly = extractDateOnlyMidnightUtc(value);

  if (dateOnly) {
    // Date-only / midnight-UTC values represent a calendar day, not a UTC instant.
    // Use noon UTC to preserve the same day when formatted in APP_TIMEZONE.
    const dateOnlyAnchor = new Date(Date.UTC(dateOnly.year, dateOnly.month - 1, dateOnly.day, 12, 0, 0, 0));
    return new Intl.DateTimeFormat("en-US", {
      timeZone: APP_TIMEZONE,
      ...options,
    }).format(dateOnlyAnchor);
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    ...options,
  }).format(date);
}

/**
 * DOCU: BC city base names for location display — "Prince George" → "Prince George BC". <br>
 * Derived from idl-po-prod projects list (225 projects). Keep in sync with backend/production/configs/constants/app.constant.ts BC_CITIES <br>
 * Last Updated Date: September 01, 2026
 * @author Sam
 */
export const BC_CITIES: ReadonlySet<string> = new Set<string>([
    'Prince George',
    'Prince Rupert',
    'Kamloops',
    'Revelstoke',
    'Terrace',
    'Quesnel',
    'Fraser Lake',
    'Dawson Creek',
    'Burns Lake',
    'Houston',
    'Lax Kw\'alaams',
    'Lax Kwalaams',
    'Kitkatla',
    'Kitimat',
    'Merritt',
    'Sooke',
    'Granisle',
    'Red Rock',
    'Steward',
    'Campbell River',
    'Laxgalts\'ap',
    'Laxgaltsap',
    'Fort St John',
    'Vanderhoof',
    'Lumby',
    'Ridley Island',
    'Gitxaala',
    'Gitwinksihlkw',
    'Gitax\'taamiks Village',
    'Gitmaxmak\'ay Village',
    'Gitwinksihlkw village',
    'Cortes Island',
    'Hudson Hope',
]);

/**
 * DOCU: Non-Canada location substrings — never append BC. <br>
 * Last Updated Date: 2026-09-01
 * @author Sam
 */
export const NON_CANADA_LOCATION_SUBSTRINGS: readonly string[] = [
    'philippines',
    'cayman',
    'la union',
    'san vicente',
] as const;
